import { useCallback, useState } from 'react'
import { addDays } from 'date-fns/addDays'
import { addMonths } from 'date-fns/addMonths'
import { format } from 'date-fns/format'
import { parse } from 'date-fns/parse'
import {
  Span,
  Button,
  DatePicker,
  TravelerSelection,
  PlainButton,
  cn
} from '@pcln/horizon'
import type { ITravelerInfo } from '@pcln/horizon'
import type { DateRange } from 'react-day-picker'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import useTabGroup from '@/hooks/useTabGroup'
import AutocompleteWrapper from '@/components/AutocompleteWrapper/AutocompleteWrapper'
import logSearchFormAnalytics from '@/shared-utils/log-search-form-analytics'
import { TRAVELER_SELECTION_COLLISION_AVOIDANCE } from '@/components/SearchForm/constants'

import type { LOCATION_SEARCH_TYPE, TravelerState } from '@/types'
import useMultiHotelFormInitialValues from './useMultiHotelFormInitialValues'
import useMultiHotelFormSubmitValues from './useMultiHotelFormSubmitValues'
import {
  addDestination,
  removeDestination,
  setHotels,
  handleSubmitForm
} from '../../SearchForm/MultiHotelSearchForm/utils'
import { searchFormSchema } from '../../SearchForm/MultiHotelSearchForm/validation'
import {
  getFormContainerClass,
  getGridContainerClass,
  getInputClass
} from './HorizonMultiHotelForm.variants'
import type {
  MultiHotelFormState,
  HotelDetail
} from '../../SearchForm/MultiHotelSearchForm/types'

type MultiHotelFormProps = {
  pageName?: 'homepage' | 'landing'
}

export default function MultiHotelForm({
  pageName = 'homepage'
}: MultiHotelFormProps) {
  const { registerTabElement } = useTabGroup()
  const { initialValues, isMobile, cguid } = useMultiHotelFormInitialValues()
  const [activeHotelState, setActiveHotelState] = useState<{
    [hotelId: number]: {
      isSelectingStart: boolean
      isSelectingEnd: boolean
      tempStartDate?: Date
      tempEndDate?: Date
    }
  }>({})

  const {
    clearErrors,
    control,
    setValue,
    handleSubmit,
    watch,
    trigger,
    formState: { errors }
  } = useForm<MultiHotelFormState>({
    defaultValues: initialValues,
    mode: 'onSubmit',
    resolver: yupResolver(searchFormSchema),
    reValidateMode: 'onChange'
  })

  const { watchHotels } = useMultiHotelFormSubmitValues({
    control,
    watch,
    clearErrors,
    trigger,
    initialValues
  })

  const onCalendarChange = useCallback(
    (dates: DateRange | undefined, index: number): void => {
      const hotel = watchHotels[index]
      const hotelId = hotel.id

      // Initialize hotel state if it doesn't exist
      if (!activeHotelState[hotelId]) {
        setActiveHotelState(prev => ({
          ...prev,
          [hotelId]: {
            isSelectingStart: true,
            isSelectingEnd: false
          }
        }))
      }

      if (dates?.from) {
        // If we have both from and to dates, and they're different, it's a complete range
        if (dates?.to && dates.to.getTime() !== dates.from.getTime()) {
          // Only update form state when we have a complete range to prevent render issues
          const updates = {
            startDate: format(dates.from, 'yyyyMMdd'),
            endDate: format(dates.to, 'yyyyMMdd')
          }

          const result = setHotels(watchHotels, updates, index)
          setValue('hotels', result, { shouldDirty: true })
          setActiveHotelState(prev => ({
            ...prev,
            [hotelId]: {
              isSelectingStart: false,
              isSelectingEnd: false
            }
          }))
        } else {
          // Only from date - update visual state but don't update form state yet
          setActiveHotelState(prev => ({
            ...prev,
            [hotelId]: {
              isSelectingStart: false,
              isSelectingEnd: true,
              tempStartDate: dates.from
            }
          }))
        }
      }
    },
    [setValue, watchHotels, activeHotelState]
  )

  const currentDate = new Date()
  const getSelectedDates = useCallback(
    (hotel: HotelDetail) => {
      const hotelState = activeHotelState[hotel.id]

      const fromDate =
        hotelState?.tempStartDate ||
        (hotel.startDate
          ? parse(hotel.startDate, 'yyyyMMdd', new Date())
          : undefined)

      const toDate =
        hotelState?.tempEndDate ||
        (hotel.endDate && hotel.endDate !== hotel.startDate
          ? parse(hotel.endDate, 'yyyyMMdd', new Date())
          : undefined)

      return { from: fromDate, to: toDate }
    },
    [activeHotelState]
  )

  const onItemSelect = useCallback(
    (item: LOCATION_SEARCH_TYPE | null, index: number) => {
      const result = setHotels(watchHotels, { endLocation: item }, index)
      setValue('hotels', result, { shouldDirty: true })
      if (isMobile && !!item && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
    },
    [isMobile, setValue, watchHotels]
  )

  const handleAddHotel = useCallback(() => {
    const result = addDestination(watchHotels)
    if (result) {
      setValue('hotels', result, { shouldDirty: true })
    }
  }, [setValue, watchHotels])

  const handleRemoveHotel = useCallback(
    (index: number) => {
      const result = removeDestination(watchHotels, index)
      setValue('hotels', result, { shouldDirty: true })
      clearErrors(`hotels.${index}`)
    },
    [setValue, clearErrors, watchHotels]
  )

  const handleTravelerChange = useCallback(
    (travelerInfo: ITravelerInfo): void => {
      setValue('travelers', travelerInfo as TravelerState)
    },
    [setValue]
  )

  const handleSubmitHelper = handleSubmit((data: MultiHotelFormState) => {
    handleSubmitForm(data)
    logSearchFormAnalytics(cguid, pageName, 'multi-hotel', isMobile)
  })

  return (
    <form
      className="w-auto"
      aria-label="horizon-multi-hotel-search-form"
      onSubmit={handleSubmitHelper}
    >
      <div className={getFormContainerClass()}>
        <div className={getGridContainerClass()}>
          <div className={getInputClass('repeatFormControls')}>
            <div className={getInputClass('hotelMulti')}>
              {watchHotels?.map((hotel: HotelDetail, index: number) => (
                <div
                  key={`hotel-${hotel.id}`}
                  className="w-full flex flex-col gap-3"
                >
                  <div className="pt-1 flex flex-row items-center justify-start gap-3">
                    <Span className="text-sm font-bold">Hotel {index + 1}</Span>
                    {index > 0 && (
                      <PlainButton
                        onClick={() => handleRemoveHotel(index)}
                        type="primary"
                        className="font-bold"
                      >
                        Remove
                      </PlainButton>
                    )}
                  </div>
                  <div className="hotel-inputs grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="w-full">
                      <AutocompleteWrapper
                        label="Where to?"
                        isMobile={isMobile}
                        errorMessage={
                          errors?.hotels?.[index]?.endLocation?.message
                        }
                        searchKey={`hotels.${index}.endLocation`}
                        initialSelectedItem={
                          hotel.endLocation as LOCATION_SEARCH_TYPE | undefined
                        }
                        typeAheadEndpoint="hotels"
                        onItemSelect={itemSelected => {
                          onItemSelect(itemSelected, index)
                        }}
                        ref={(elem: HTMLInputElement) => {
                          registerTabElement(elem)
                        }}
                      />
                    </div>
                    <div className={getInputClass('datePicker')}>
                      <DatePicker
                        key={`datepicker-${hotel.id}-${hotel.startDate}-${hotel.endDate}`}
                        isMobile={isMobile}
                        className="w-full"
                        title="Hotels: Check-in - Check-out"
                        label="Check-in - Check-out"
                        mode="range"
                        numberOfMonths={isMobile ? 13 : 2}
                        defaultMonth={
                          hotel.startDate
                            ? parse(hotel.startDate, 'yyyyMMdd', new Date())
                            : undefined
                        }
                        handleCalendarChange={dates => {
                          onCalendarChange(dates, index)
                        }}
                        errorMessage={
                          errors?.hotels?.[index]?.startDate?.message ||
                          errors?.hotels?.[index]?.endDate?.message
                        }
                        inputRef={registerTabElement}
                        selected={getSelectedDates(hotel)}
                        disabled={{
                          before: currentDate,
                          after:
                            activeHotelState[index]?.tempStartDate &&
                            !activeHotelState[index]?.tempEndDate
                              ? addDays(
                                  activeHotelState[index].tempStartDate,
                                  28
                                )
                              : undefined
                        }}
                        startMonth={currentDate}
                        endMonth={addMonths(currentDate, 12)}
                        allowSameDayInRange={false}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <Button
            className={getInputClass('addHotelButton')}
            onClick={handleAddHotel}
            disabled={watchHotels?.length > 4}
          >
            Add Another Hotel
          </Button>
          <div className={getInputClass('travelersSubmitContainer')}>
            <div className="w-full translate-y-0">
              <TravelerSelection
                isMobile={isMobile}
                isHotel
                initialValues={initialValues.travelers}
                roomsEnabled
                maxRooms={9}
                maxChildren={8}
                maxTravelers={36}
                ageSelectEnabled
                tooltipErrorMessageId="travelerSelectionError"
                errorMessage={
                  errors?.travelers?.children?.message ||
                  errors?.travelers?.rooms?.message ||
                  ''
                }
                onChange={handleTravelerChange}
                collisionAvoidance={TRAVELER_SELECTION_COLLISION_AVOIDANCE}
              />
            </div>
            <Button
              buttonType="submit"
              type="primary"
              size="lg"
              className={cn(
                isMobile
                  ? 'fixed bottom-4 right-4 left-4'
                  : getInputClass('submit')
              )}
            >
              Search
            </Button>
          </div>
        </div>
      </div>
    </form>
  )
}
